import { FC } from 'react';
import classNames from 'classnames';
import Select from 'react-select';
import { ENV_CONFIG } from 'config/environment';
import {
  DragNDropRecExpanded,
  RecExpandedPageRow,
} from 'components/DragNDrop/DragNDropRecExpanded';
import { IPage } from 'store/pages/types';
import { AllPagesByType, flattenPagesByType, normalizeSetPages } from '../utils';

const PAGE_TYPE_TO_KEY: Record<string, keyof AllPagesByType> = {
  DONATION: 'donate',
  EVENT: 'event',
  RECOMMENDATION: 'recommendation',
};

function pageToOption(p: IPage) {
  return { value: p.query_str, label: p.value };
}

function getPageTypeLabel(page: IPage): string {
  if (page.event_id) return 'Event Page';
  if (page.page_type === 'REC') return 'Recommendation Page';
  return 'Donation Page';
}

function getPageImageUrl(page: IPage): string {
  const base = ENV_CONFIG().CLOUD_STORAGE.URL;
  const tags = ENV_CONFIG().CLOUD_STORAGE.TAGS;
  return `${base}/${tags}/icon/tag_${page.id}.png`;
}

function setPagesToRecExpandedRows(
  setPages: Record<string, unknown> | undefined,
  allPagesFlat: IPage[]
): RecExpandedPageRow[] {
  if (!setPages || typeof setPages !== 'object') return [];
  return Object.keys(setPages).map(queryStr => {
    const page = allPagesFlat.find(p => p.query_str === queryStr);
    return {
      value: queryStr,
      label: page?.value ?? queryStr,
      imageUrl: page ? getPageImageUrl(page) : '',
      description: page ? getPageTypeLabel(page) : '',
    };
  });
}

interface SetPagesFormProps {
  input: any;
  meta: any;
  formState: any;
  allPagesByType: AllPagesByType;
  allPagesLoading: boolean;
}

export const SetPagesForm: FC<SetPagesFormProps> = ({
  input,
  meta,
  formState,
  allPagesByType,
  allPagesLoading,
}) => {
  const setPages = normalizeSetPages(input.value ?? {});
  const pageType = (formState.getState().values?.page_type as string) || 'DONATION';
  const pageTypeKey = PAGE_TYPE_TO_KEY[pageType] ?? 'donate';
  const pagesOfType = allPagesByType[pageTypeKey] ?? [];
  const pagesOptions = pagesOfType.map(pageToOption);
  const allPagesFlat: IPage[] = flattenPagesByType(allPagesByType);
  const pagesSelectedAsOptions = Object.keys(setPages).map(qs => {
    const page = allPagesFlat.find(p => p.query_str === qs);
    return { value: qs, label: page?.value ?? qs };
  });
  const pagesSelectedRows = setPagesToRecExpandedRows(setPages, allPagesFlat);

  const addPages = (newOptions: { value: string; label: string }[]) => {
    const next = { ...setPages };
    newOptions.forEach(opt => {
      next[opt.value] = true;
    });
    formState.change('set_pages', next);
  };

  const deletePage = (queryStr: string) => {
    const next = { ...setPages };
    delete next[queryStr];
    formState.change('set_pages', next);
  };

  const setPagesSelected = (newRows: RecExpandedPageRow[]) => {
    const next = newRows.reduce<Record<string, unknown>>((acc, row) => {
      acc[row.value] = true;
      return acc;
    }, {});
    formState.change('set_pages', next);
  };

  const hasError = meta.error && meta.touched;

  return (
    <>
      <div className={classNames('form-group', hasError ? 'has-danger' : '')}>
        <Select
          className={classNames('react-select', hasError ? 'error' : 'success')}
          classNamePrefix="react-select"
          placeholder={allPagesLoading ? 'Loading pages...' : 'Search'}
          isMulti
          isSearchable
          closeMenuOnSelect
          hideSelectedOptions
          isClearable={false}
          value={pagesSelectedAsOptions}
          options={pagesOptions}
          onChange={addPages}
          onBlur={input.onBlur}
          onFocus={input.onFocus}
          isDisabled={allPagesLoading}
        />
        {hasError && (
          <label htmlFor="set_pages" className="error">
            {meta.error}
          </label>
        )}
      </div>
      <div className="oath-utility gray">
        Can’t find a page? Please complete your page creation and email support@oath.vote for
        assistance.
      </div>
      {pagesSelectedRows.length > 0 && (
        <>
          <br />
          <div className="oath-utility gray">
            By default, pages will appear in the ranked order below. You can adjust page order
            below.
          </div>
          <DragNDropRecExpanded
            rows={pagesSelectedRows}
            setRows={setPagesSelected}
            deleteRow={deletePage}
            withDetails={false}
            variant="page"
          />
        </>
      )}
    </>
  );
};
